import { Params, Router } from '@angular/router';

/**
 * Shared helper for the "returnTo" query-param navigation pattern.
 *
 * When a related record (Kunde / Standort / Kontakt) is opened from a
 * Hausverwaltung edit page, we attach return context so the back button on the
 * target page can navigate back to that same Hausverwaltung (and tab).
 */

export const HAUSVERWALTUNG_RETURN = 'hausverwaltung-edit';

export interface HausverwaltungReturn {
  hvId: string;
  tab: number | null;
}

/**
 * Build the query params used when navigating away from a Hausverwaltung edit
 * page. Returns an empty object when no Hausverwaltung id is available.
 */
export function buildHausverwaltungReturnParams(
  hvId: string | null | undefined,
  tab: number | null | undefined
): Params {
  if (!hvId) {
    return {};
  }
  const params: Params = {
    returnTo: HAUSVERWALTUNG_RETURN,
    hausverwaltungId: hvId,
  };
  if (tab != null) {
    params.returnTab = tab;
  }
  return params;
}

/**
 * Parse the return context out of the current route query params.
 * Returns null when the params do not describe a Hausverwaltung return.
 */
export function parseHausverwaltungReturn(queryParams: Params): HausverwaltungReturn | null {
  if (!queryParams || queryParams['returnTo'] !== HAUSVERWALTUNG_RETURN) {
    return null;
  }
  const hvId = queryParams['hausverwaltungId'];
  if (!hvId) {
    return null;
  }
  const rawTab = queryParams['returnTab'];
  const tab = rawTab != null && rawTab !== '' ? Number(rawTab) : null;
  return { hvId, tab: tab != null && !Number.isNaN(tab) ? tab : null };
}

/**
 * Navigate back to a Hausverwaltung edit page, restoring the given tab.
 */
export function navigateBackToHausverwaltung(
  router: Router,
  hvId: string,
  tab?: number | null
): void {
  router.navigate(['/hausverwaltungen/edit', hvId], {
    queryParams: tab != null ? { activeTab: tab } : {},
  });
}
